/* grinny storefront bridge. Public configuration only; never put private keys here. */
(function (global) {
  'use strict';

  const EVENT_NAMES = new Set(['reserve_click', 'product_selected', 'offer_viewed', 'deposit_checkout_clicked', 'form_error', 'color_selected', 'size_selected', 'refill_selected', 'plan_selected', 'flavor_selected', 'formula_color_selected', 'email_submitted', 'referral_share']);
  const ANALYTICS_FIELDS = ['product', 'color', 'size', 'refill', 'plan', 'cadence', 'headSize', 'flavor', 'formulaColor', 'source', 'method', 'testVersion', 'offerId', 'utmSource', 'utmMedium', 'utmCampaign', 'explicitChoices', 'section', 'device', 'reservation_id', 'event_id'];
  const PRODUCTS = new Set(['tablets', 'paste'].flatMap(system => ['home', 'mini', 'duo', 'four', 'solo', 'pair', 'family'].map(kind => system + '-' + kind)));
  const FORMULA_COLORS = new Set(['original', 'purple']);
  const PLANS = new Set(['none', 'solo', 'pair', 'family']);
  const COLORS = new Set(['coral', 'white', 'black', 'navy', 'mixed']);
  const HEAD_SIZES = new Set(['Home', 'Mini', 'Mixed home + mini']);
  const REFILLS = new Set(['tablets', 'paste']);
  const FLAVORS = new Set(['mint', 'strawberry', 'citrus', 'variety']);
  const PENDING_KEY = 'grinny:native-pending:v4';
  const REFERRAL_KEY = 'grinny:referral:v1';
  const RESERVATION_KEY = 'grinny:reservation:v1';
  const DEFAULTS = {
    mode: 'preview', klaviyoSiteId: '', klaviyoListId: '',
    klaviyoRevision: '2026-07-15', klaviyoDoubleOptIn: true,
    nativeFormId: 'grinny-native-reservation', nativeDoubleOptIn: true,
    depositEnabled: false, depositVariantId: '', depositProductHandle: '',
    referralBaseUrl: '', supportEmail: '',
    launchConsentText: 'Email me about my reservation, first access and the grinny launch. I can unsubscribe anytime.'
  };
  const configElement = global.document && document.getElementById('grinny-integration-config');
  let supplied = {};
  try { supplied = configElement ? JSON.parse(configElement.textContent) : (global.GRINNY_CONFIG || {}); } catch (_) {}
  const config = Object.assign({}, DEFAULTS, supplied);
  let depositInFlight = null;
  let nativeResult = null;
  let privacyLoading = null;
  let trackingChoice;

  function integrationError(code, message) {
    const error = new Error(message);
    error.code = code;
    return error;
  }
  function short(value, max) { return String(value || '').trim().slice(0, max || 100); }
  function attribution(value) { const v = short(value, 80); return /^[a-zA-Z0-9_.-]{1,80}$/.test(v) ? v : ''; }
  function explicitChoices(value) { const allowed = new Set(['product','color','size','flavor','formulaColor','plan','headSize']); return [...new Set(short(value, 100).split(',').filter(v => allowed.has(v)))].sort().join(','); }
  function sessionGet(key) { try { return sessionStorage.getItem(key); } catch (_) { return null; } }
  function sessionSet(key, value) { try { sessionStorage.setItem(key, value); } catch (_) {} }
  function sessionRemove(key) { try { sessionStorage.removeItem(key); } catch (_) {} }
  function identifier() {
    return Array.from(global.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
  }
  function reservationId() {
    let value = sessionGet(RESERVATION_KEY);
    if (!/^[a-f0-9]{32}$/.test(value || '')) { value = identifier(); sessionSet(RESERVATION_KEY, value); }
    return value;
  }
  function referralLink(id) {
    const url = new URL(config.referralBaseUrl || global.location.origin + global.location.pathname);
    url.search = '';
    url.hash = '';
    url.searchParams.set('ref', short(id, 64));
    return url.toString();
  }
  function referredBy() {
    const query = new URLSearchParams(global.location.search).get('ref');
    if (query && /^[a-zA-Z0-9_-]{8,64}$/.test(query)) sessionSet(REFERRAL_KEY, query);
    return sessionGet(REFERRAL_KEY) || '';
  }
  function preferences(input) {
    const data = {
      product: short(input.product, 60), color: short(input.color).toLowerCase(),
      size: short(input.size, 60), refill: short(input.refill, 60).toLowerCase(),
      plan: short(input.plan, 30), cadence: short(input.cadence, 30),
      headSize: short(input.headSize, 60),
      flavor: short(input.flavor == null ? 'mint' : input.flavor, 30),
      formulaColor: short(input.formulaColor == null ? 'original' : input.formulaColor, 30),
      source: attribution(input.source || 'reservation-modal'),
      testVersion: 'grinny-v6-equal-pack', offerId: '30d-18-24-44',
      utmSource: attribution(input.utmSource), utmMedium: attribution(input.utmMedium), utmCampaign: attribution(input.utmCampaign),
      explicitChoices: explicitChoices(input.explicitChoices),
      reservationId: /^[a-zA-Z0-9_-]{8,64}$/.test(input.reservationId || '') ? input.reservationId : reservationId()
    };
    if (!PRODUCTS.has(data.product) || !data.size || !REFILLS.has(data.refill) || !COLORS.has(data.color) || !PLANS.has(data.plan) || !HEAD_SIZES.has(data.headSize) || !FLAVORS.has(data.flavor) || !FORMULA_COLORS.has(data.formulaColor)) {
      throw integrationError('INVALID_SELECTION', 'Choose a product, color, size, refill, flavor, plan preference and head size.');
    }
    const [system, kind] = data.product.split('-');
    const allowedSizes = {home: ['Home'], mini: ['Mini'], duo: ['Home + mini'], four: ['4 Home']};
    const sizes = allowedSizes[kind] || ['Home', 'Mini', 'Mixed home + mini'];
    if ((kind === 'four') !== (data.color === 'mixed') || system !== data.refill || !sizes.includes(data.size) || (data.plan === 'solo' && data.headSize === 'Mixed home + mini')) {
      throw integrationError('INVALID_SELECTION', 'Choose the matching brush product, refill and size.');
    }
    if (data.cadence !== (data.plan === 'none' ? 'none' : '30-days') || (PLANS.has(kind) && data.plan !== kind)) {
      throw integrationError('INVALID_SELECTION', 'Choose the matching refill plan and delivery preference.');
    }
    return data;
  }
  function loadPrivacyApi() {
    if (global.Shopify && global.Shopify.customerPrivacy) return Promise.resolve(global.Shopify.customerPrivacy);
    if (privacyLoading) return privacyLoading;
    privacyLoading = new Promise((resolve, reject) => {
      if (!global.Shopify || typeof global.Shopify.loadFeatures !== 'function') {
        reject(integrationError('PRIVACY_UNAVAILABLE', 'Cookie preferences could not be saved. Please try again.'));
        return;
      }
      const timer = global.setTimeout(() => reject(integrationError('PRIVACY_UNAVAILABLE', 'Cookie preferences could not be saved. Please try again.')), 10000);
      global.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        global.clearTimeout(timer);
        if (error || !global.Shopify.customerPrivacy) reject(integrationError('PRIVACY_UNAVAILABLE', 'Cookie preferences could not be saved. Please try again.'));
        else resolve(global.Shopify.customerPrivacy);
      });
    }).finally(() => { privacyLoading = null; });
    return privacyLoading;
  }
  async function setConsent(allowed) {
    if (typeof allowed !== 'boolean') throw integrationError('INVALID_CONSENT', 'Choose your cookie preference.');
    // Called only from an explicit banner/preferences action, never automatically.
    // Block local forwarding immediately on decline, including while the API loads.
    if (!allowed) trackingChoice = false;
    if (config.mode !== 'live') {
      trackingChoice = allowed;
      return { status: 'preview', analytics: allowed, marketing: allowed, preferences: allowed };
    }
    const privacy = await loadPrivacyApi();
    return new Promise((resolve, reject) => {
      const timer = global.setTimeout(() => reject(integrationError('PRIVACY_UNAVAILABLE', 'Cookie preferences could not be saved. Please try again.')), 10000);
      privacy.setTrackingConsent({ analytics: allowed, marketing: allowed, preferences: allowed }, () => {
        global.clearTimeout(timer);
        trackingChoice = allowed;
        const result = { status: 'saved', analytics: privacy.analyticsProcessingAllowed(), marketing: privacy.marketingAllowed(), preferences: privacy.preferencesProcessingAllowed() };
        global.dispatchEvent(new CustomEvent('grinny:consent', { detail: result }));
        resolve(result);
      });
      // sale_of_data and GPC remain separate. Accepting cookies never overrides
      // an existing sale/sharing opt-out or the browser's GPC signal.
    });
  }
  function track(name, input) {
    // deposit_paid is intentionally excluded. Only trusted paid-order handling may emit it.
    if (!EVENT_NAMES.has(name)) return false;
    const data = {};
    ANALYTICS_FIELDS.forEach(key => { if (input && input[key] != null) data[key] = short(input[key], 100); });
    ['utmSource','utmMedium','utmCampaign'].forEach(key => { if (key in data) data[key] = attribution(data[key]); });
    if ('explicitChoices' in data) data.explicitChoices = explicitChoices(data.explicitChoices);
    data.event_id = data.event_id || identifier();
    data.schema_version = 'grinny-v5';
    const detail = { event: name, ...data };
    global.dispatchEvent(new CustomEvent('grinny:analytics', { detail }));
    if (config.mode === 'live' && trackingChoice !== false && global.Shopify && global.Shopify.analytics && global.Shopify.analytics.publish) {
      global.Shopify.analytics.publish(name, data);
    }
    // No raw email, query string, arbitrary user text, or ad SDK calls on the storefront.
    // Connected Shopify Customer Events pixels own consent and dispatch to ad platforms.
    return detail;
  }
  function subscriptionPayload(input, data) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'grinny pre-launch reservation; launch updates only',
          profile: { data: { type: 'profile', attributes: {
            email: input.email.trim(),
            properties: {
              grinny_product: data.product, grinny_color: data.color,
              grinny_size: data.size, grinny_refill: data.refill,
              grinny_plan: data.plan, grinny_cadence: data.cadence,
              grinny_head_size: data.headSize,
              grinny_flavor: data.flavor, grinny_formula_color: data.formulaColor,
              grinny_plan_interest_only: true,
              grinny_reservation_id: data.reservationId,
              grinny_referral_code: data.reservationId,
              grinny_referral_url: referralLink(data.reservationId),
              grinny_referred_by: referredBy(),
              grinny_source: data.source,
              grinny_test_version: data.testVersion, grinny_offer_id: data.offerId,
              grinny_utm_source: data.utmSource, grinny_utm_medium: data.utmMedium, grinny_utm_campaign: data.utmCampaign,
              grinny_explicit_choices: data.explicitChoices,
              grinny_consent_scope: 'reservation-and-launch-updates',
              grinny_consent_text: config.launchConsentText,
              grinny_consent_recorded_at: new Date().toISOString()
            },
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: config.klaviyoListId } } }
      }
    };
  }
  function nativeTags(data) {
    const slug = value => short(value, 70).toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
    return ['grinny-waitlist', 'grinny-consent-launch-only',
      'grinny-product-' + slug(data.product), 'grinny-color-' + slug(data.color),
      'grinny-size-' + slug(data.size), 'grinny-refill-' + slug(data.refill),
      'grinny-plan-' + slug(data.plan), 'grinny-cadence-' + slug(data.cadence),
      'grinny-head-size-' + slug(data.headSize), 'grinny-plan-interest-only',
      'grinny-flavor-' + slug(data.flavor), 'grinny-formula-color-' + slug(data.formulaColor),
      'grinny-referral-' + slug(data.reservationId),
      'grinny-test-' + slug(data.testVersion), 'grinny-offer-' + slug(data.offerId),
      'grinny-source-' + slug(data.source),
      data.explicitChoices ? 'grinny-explicit-' + slug(data.explicitChoices) : '',
      data.utmSource ? 'grinny-utm-source-' + slug(data.utmSource) : '',
      data.utmMedium ? 'grinny-utm-medium-' + slug(data.utmMedium) : '',
      data.utmCampaign ? 'grinny-utm-campaign-' + slug(data.utmCampaign) : '',
      referredBy() ? 'grinny-referred-by-' + slug(referredBy()) : ''
    ].filter(Boolean).join(',');
  }
  function nativeSubmit(input, data) {
    const form = document.getElementById(config.nativeFormId);
    if (!form || form.getAttribute('data-grinny-native') !== 'true') {
      throw integrationError('NOT_CONFIGURED', 'Reservations are opening soon. Please check back shortly.');
    }
    const email = form.querySelector('[name="contact[email]"]');
    const tags = form.querySelector('[name="contact[tags]"]');
    if (!email || !tags) throw integrationError('NATIVE_FORM_INVALID', 'The reservation form could not be loaded. Please try again.');
    email.value = input.email.trim();
    tags.value = nativeTags(data);
    // Remember only product choices and an opaque ID across the normal Shopify form POST.
    sessionSet(PENDING_KEY, JSON.stringify({ ...data, at: Date.now() }));
    form.requestSubmit();
    return { status: 'redirecting' };
  }
  async function reserve(input) {
    if (config.mode !== 'live') throw integrationError('PREVIEW_ONLY', 'This is a preview. No reservation has been submitted.');
    if (!input || String(input.email || '').trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(short(input.email, 254))) {
      throw integrationError('INVALID_EMAIL', 'Enter a valid email address.');
    }
    if (input.consent !== true) {
      throw integrationError('CONSENT_REQUIRED', 'Please agree to receive reservation and launch emails.');
    }
    const data = preferences(input);
    if (!(config.klaviyoSiteId && config.klaviyoListId)) return nativeSubmit(input, data);
    const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(config.klaviyoSiteId), {
      method: 'POST',
      headers: { 'content-type': 'application/vnd.api+json', 'accept': 'application/vnd.api+json', revision: config.klaviyoRevision },
      body: JSON.stringify(subscriptionPayload(input, data))
    });
    if (!response.ok) {
      // Do not silently redirect into a second consent/list path when a configured API fails.
      throw integrationError('RESERVATION_FAILED', 'We could not save your reservation. Please try again.');
    }
    track('email_submitted', { ...data, reservation_id: data.reservationId, method: 'klaviyo' });
    return { status: 'accepted', ...data, referralUrl: referralLink(data.reservationId), needsConfirmation: config.klaviyoDoubleOptIn !== false };
  }
  function consumeNativeResult() {
    if (nativeResult) { const result = nativeResult; nativeResult = null; return result; }
    return null;
  }
  function resolveNativeSuccess() {
    const success = document.getElementById('grinny-native-success');
    if (!success) return;
    let data;
    try { data = JSON.parse(sessionGet(PENDING_KEY) || 'null'); } catch (_) {}
    sessionRemove(PENDING_KEY);
    if (!data || Date.now() - data.at > 30 * 60 * 1000) return;
    let clean;
    try { clean = preferences(data); } catch (_) { return; }
    nativeResult = { status: 'accepted', confirmationUnknown: true, ...clean, referralUrl: referralLink(clean.reservationId), needsConfirmation: config.nativeDoubleOptIn !== false };
    track('email_submitted', { ...clean, reservation_id: clean.reservationId, method: 'shopify-native' });
    global.dispatchEvent(new CustomEvent('grinny:native-reserved', { detail: nativeResult }));
  }
  async function jsonRequest(url, options) {
    const response = await fetch(url, options);
    let payload;
    try { payload = await response.json(); } catch (_) { throw integrationError('CHECKOUT_ERROR', 'Checkout could not be loaded. Please try again.'); }
    if (!response.ok) throw integrationError('CHECKOUT_ERROR', 'Checkout could not be loaded. Please try again.');
    return payload;
  }
  function postOptions(body) {
    return { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) };
  }
  async function startDeposit(input) {
    if (config.mode !== 'live' || !config.depositEnabled || !/^\d+$/.test(String(config.depositVariantId)) || !config.depositProductHandle) {
      throw integrationError('DEPOSIT_NOT_CONFIGURED', 'Your free reservation is saved. Deposits are not open yet.');
    }
    if (!config.supportEmail || !global.Shopify || !global.Shopify.routes || !global.Shopify.routes.root) {
      throw integrationError('DEPOSIT_NOT_CONFIGURED', 'Your free reservation is saved. Deposits are not open yet.');
    }
    const data = preferences(input);
    const root = global.Shopify.routes.root;
    const [cart, product] = await Promise.all([
      jsonRequest(root + 'cart.js'),
      jsonRequest(root + 'products/' + encodeURIComponent(config.depositProductHandle) + '.js')
    ]);
    const variant = (product.variants || []).find(v => String(v.id) === String(config.depositVariantId));
    if (cart.currency !== 'USD' || !variant || !variant.available || variant.requires_shipping !== false || variant.price !== 1000 || variant.requires_selling_plan === true || product.requires_selling_plan === true) {
      throw integrationError('DEPOSIT_UNAVAILABLE', 'The $10 reservation deposit is currently unavailable. Your free reservation is saved.');
    }
    const existing = (cart.items || []).find(item => String(item.variant_id) === String(config.depositVariantId) && item.properties && item.properties._grinny_reservation_id === data.reservationId);
    if ((cart.items || []).some(item => !existing || item.key !== existing.key)) {
      throw integrationError('CART_REVIEW_REQUIRED', 'Your cart contains another item. Review your cart before adding this reservation deposit.');
    }
    const properties = {
      Product: data.product, Color: data.color, Size: data.size, Refill: data.refill,
      'Plan interest': data.plan, 'Cadence preference': data.cadence, 'Head size': data.headSize,
      Flavor: data.flavor, 'Refill color': data.formulaColor,
      'Reservation terms': 'Fully refundable anytime. No product ships now. No subscription or recurring charge starts.',
      _grinny_reservation_id: data.reservationId,
      _grinny_referral_code: data.reservationId,
      _grinny_referred_by: referredBy(),
      _grinny_type: 'refundable-launch-deposit',
      _grinny_test_version: data.testVersion, _grinny_offer_id: data.offerId, _grinny_source: data.source,
      _grinny_explicit_choices: data.explicitChoices,
      _grinny_utm_source: data.utmSource, _grinny_utm_medium: data.utmMedium, _grinny_utm_campaign: data.utmCampaign
    };
    if (existing) {
      await jsonRequest(root + 'cart/change.js', postOptions({ id: existing.key, quantity: 1, properties }));
    } else {
      await jsonRequest(root + 'cart/add.js', postOptions({ items: [{ id: config.depositVariantId, quantity: 1, properties }] }));
    }
    const finalCart = await jsonRequest(root + 'cart.js');
    if (finalCart.currency !== 'USD' || finalCart.items.length !== 1 || String(finalCart.items[0].variant_id) !== String(config.depositVariantId) || finalCart.items[0].quantity !== 1 || finalCart.total_price !== 1000 || finalCart.items[0].selling_plan_allocation) {
      throw integrationError('CART_REVIEW_REQUIRED', 'Please review your cart before continuing. The reservation deposit should be exactly $10.');
    }
    // Adding to cart is not a payment. No paid event or paid UI is triggered here.
    global.location.assign(root + 'checkout');
    return { status: 'redirecting' };
  }
  function checkoutDeposit(input) {
    if (depositInFlight) return depositInFlight;
    depositInFlight = startDeposit(input).finally(() => { depositInFlight = null; });
    return depositInFlight;
  }

  global.GrinnyIntegrations = Object.freeze({ config, track, reserve, checkoutDeposit, referralLink, consumeNativeResult, setConsent });
  referredBy();
  if (global.document) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resolveNativeSuccess, { once: true });
    else resolveNativeSuccess();
  }
})(window);
